import { Button, DatePicker, Form, Modal, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Cookies from "js-cookie";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { useChatContext } from "../../../context/ChatContext";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { openNotificationSuccess } from "../../../utils/Utils";
import { conversationType } from "../../../config/Constant";
import { useNavigate } from "react-router-dom";
dayjs.extend(utc);

const ChatScheduleModal = ({ onCancel, open, onConfirm, friendId }) => {
  const navigate = useNavigate();
  const { user, languageMap } = useInfoUser();

  const { selectedConversation } = useMenuContext();
  const { scheduleMessage } = useChatContext();

  const [form] = Form.useForm();
  const [schedule, setSchedule] = useState({
    requestUuid: uuidv4(),
    triggerId: scheduleMessage?.triggerId || uuidv4(),
    actionType: scheduleMessage ? "UPDATE_MESSAGE" : "CREATE_MESSAGE",
    content: scheduleMessage?.content,
    time: "",
    repeatType: scheduleMessage?.repeatType
      ? scheduleMessage?.repeatType
      : "ONE_TIME",
  });

  const repeatType = useCallback(
    [
      {
        value: "ONE_TIME",
        label: languageMap?.["modal.scheduleModal.oneTime"],
      },
      {
        value: "DAY",
        label: languageMap?.["modal.scheduleModal.everyDay"],
      },
      {
        value: "WEEK",
        label: languageMap?.["modal.scheduleModal.everyWeek"],
      },
      {
        value: "MONTH",
        label: languageMap?.["modal.scheduleModal.everyMonth"],
      },
    ],
    [languageMap],
  );

  const onFinish = async (values) => {
    let createdConversation = null;

    if (friendId) {
      const userIds =
        user?.userId === friendId ? [user?.userId] : [user?.userId, friendId];

      createdConversation = await apiFactory.conversationApi.createGroup({
        name: "",
        userIds: userIds,
        conversationType: conversationType.PERSONAL,
      });
    }

    const utcTime = values.time.utc();

    const result = await apiFactory.messageApi.schedule({
      triggerId: schedule?.triggerId,
      second: utcTime?.second(),
      minute: utcTime?.minute(),
      hour: utcTime?.hour(),
      dayOfMonth: utcTime?.date(),
      month: utcTime?.month() + 1,
      dayOfWeek: utcTime?.day(),
      time: utcTime,
      repeatType: values?.repeatType,
      actionType: schedule?.actionType,
      data: {
        requestUuid: schedule?.requestUuid,
        triggerId: schedule?.triggerId,
        conversationId: friendId
          ? createdConversation?.data?.conversationId
          : selectedConversation?.conversationId,
        contentType: "SCHEDULE",
        content: values?.content,
        token: Cookies.get("access_token"),
        messageScheduleId: scheduleMessage?.messageScheduleId || uuidv4(),
        scheduleTime: values?.time,
        repeatType: values?.repeatType,
        scheduleStatus: "ACTIVE",
      },
    });

    if (result?.status === 200) {
      openNotificationSuccess("Create a schedule successfully");
      navigate(
        `/conversation/chat/${createdConversation?.data?.conversationId}`,
      );
    }

    onCancel();
  };

  const onDelete = async () => {
    const result = await apiFactory.messageApi.deleteSchedule({
      messageId: scheduleMessage?.messageId,
    });

    if (result?.status === 200) {
      openNotificationSuccess("Create a schedule successfully");
    }

    onCancel();
  };

  return (
    <Modal
      width={450}
      open={open}
      onCancel={() => {
        onCancel();
      }}
      footer={null}
      centered
      className="custom-confirm-modal"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        form={form}
        name="calendarForm"
        onFinish={onFinish}
        initialValues={{
          ...schedule,
        }}
      >
        <div className="modal-header">
          <div className="modal-title">
            {scheduleMessage
              ? languageMap?.["modal.scheduleModal.update"]
              : languageMap?.["modal.scheduleModal.create"]}
          </div>
          <div className="modal-divider" />
        </div>

        <div className="text-base mb-6">
          <Form.Item
            name="content"
            label={languageMap?.["modal.scheduleModal.inputText"]}
            rules={[
              {
                required: true,
                message: languageMap?.["modal.scheduleModal.pleaseInputText"],
              },
            ]}
          >
            <TextArea
              rows={2}
              maxLength={94}
              className="mb-[5px] whitespace-pre-wrap"
            />
          </Form.Item>
          <Form.Item
            name="time"
            label={languageMap?.["modal.scheduleModal.selectTime"]}
            rules={[
              {
                required: true,
                message:
                  languageMap?.["modal.scheduleModal.pleaseSelectStartDate"],
              },
            ]}
          >
            <DatePicker
              value={schedule?.time}
              showNow={false}
              showTime={{ format: "HH:mm" }}
              // needConfirm={false}
              format="YYYY-MM-DD HH:mm"
              allowClear={false}
              onKeyDown={(e) => e.preventDefault()}
              className="w-[100%] mb-[5px]"
              minDate={dayjs()}
            />
          </Form.Item>
          <Form.Item
            name="repeatType"
            label={languageMap?.["modal.scheduleModal.repeatType"]}
            rules={[
              {
                required: true,
                message:
                  languageMap?.["modal.scheduleModal.pleaseSelectStartDate"],
              },
            ]}
          >
            <Select className="w-[100%] mb-[5px]" options={repeatType} />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            className="!bg-[#ffffff] !text-black hover:!bg-[#d9d9d9] font-medium"
            onClick={onCancel}
          >
            {languageMap?.["modal.cancel"] ?? "Không"}
          </Button>
          {scheduleMessage && (
            <Button
              className="!bg-[#717479] !text-white font-medium"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          <Button
            htmlType="submit"
            className="!bg-[#ff4d4f] !text-white hover:!bg-[#ff7875] font-medium"
          >
            {languageMap?.["modal.ok"] ?? "Xóa"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export { ChatScheduleModal };
